"use client";

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { galleryMessages } from "../i18n/gallery";
import { webSiteNavigationLabels } from "../webSiteNavigation";
import { useWebMobileMenuOverlay } from "./useWebMobileMenuOverlay";
import type { TemplateCssVars } from "../types";

const HEADER_SCROLLED_ATTRIBUTE = "data-nocturne-header-scrolled";
const HERO_ACTIVE_ATTRIBUTE = "data-nocturne-slide-active";
const HERO_CUE_OPACITY_PROPERTY = "--nocturne-scroll-cue-opacity";

type GalleryItem = Readonly<{
    alt: string;
    src: string;
}>;

export function NocturneClubRuntime({
    designCssVars,
    languageCode,
}: {
    designCssVars: TemplateCssVars;
    languageCode?: string | null;
}) {
    const navigationId = useId();
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const galleryDialogRef = useRef<HTMLDivElement>(null);
    const galleryLastFocusedRef = useRef<HTMLElement | null>(null);
    const galleryPointerStartRef = useRef<number | null>(null);
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
    const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
    const labels = webSiteNavigationLabels(languageCode);
    const galleryLabels = galleryMessages(languageCode);

    const closeGallery = useCallback(() => {
        setGalleryIndex(null);
        window.requestAnimationFrame(() => {
            galleryLastFocusedRef.current?.focus({ preventScroll: true });
        });
    }, []);

    const showGalleryOffset = useCallback((offset: number) => {
        setGalleryIndex((current) => {
            if (current === null || galleryItems.length === 0) {
                return current;
            }

            return (current + offset + galleryItems.length) % galleryItems.length;
        });
    }, [galleryItems.length]);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            '[data-web-template="nocturne-club"]',
        ) ?? null;
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;

        setHeader(nextHeader);

        if (!template) {
            return;
        }

        const hero = template.querySelector<HTMLElement>(
            '[data-web-region="homeHero"]',
        );
        const scrollCue = hero?.querySelector<HTMLElement>(
            '[data-document-role="heroScrollCue"]',
        );
        const slides = Array.from(hero?.querySelectorAll<HTMLElement>(
            '[data-document-role="heroSlide"]',
        ) ?? []);
        const revealNodes = Array.from(template.querySelectorAll<HTMLElement>(
            '[data-animation="reveal"]',
        ));
        const galleryTiles = Array.from(template.querySelectorAll<HTMLElement>(
            '[data-document-role="galleryTile"]',
        ));
        const nextGalleryItems = galleryTiles.flatMap((tile) => {
            const galleryImage = tile.querySelector<HTMLImageElement>("img");
            const src = galleryImage?.currentSrc || galleryImage?.src || "";

            return src === ""
                ? []
                : [{
                    alt: galleryImage?.alt || galleryLabels.photo,
                    src,
                }];
        });
        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        let animationFrame: number | null = null;
        let activeSlide = 0;
        let slideshowTimer: number | null = null;

        const updateHeader = () => {
            animationFrame = null;
            const compactThreshold = window.matchMedia("(max-width: 900px)").matches
                ? 54
                : 110;
            const scrolled = hero === null || window.scrollY > compactThreshold;

            if (nextHeader) {
                if (scrolled) {
                    nextHeader.setAttribute(HEADER_SCROLLED_ATTRIBUTE, "true");
                } else {
                    nextHeader.removeAttribute(HEADER_SCROLLED_ATTRIBUTE);
                }
            }

            if (scrollCue) {
                const opacity = Math.max(0, Math.min(1, 1 - window.scrollY / 420));
                scrollCue.style.setProperty(
                    HERO_CUE_OPACITY_PROPERTY,
                    opacity.toFixed(3),
                );
                scrollCue.toggleAttribute("data-nocturne-scroll-cue-hidden", opacity <= 0.04);
            }
        };
        const requestHeaderUpdate = () => {
            if (animationFrame === null) {
                animationFrame = window.requestAnimationFrame(updateHeader);
            }
        };
        const showSlide = (index: number) => {
            slides.forEach((slide, slideIndex) => {
                slide.toggleAttribute(
                    HERO_ACTIVE_ATTRIBUTE,
                    slideIndex === index,
                );
            });
        };
        const startSlideshow = () => {
            if (prefersReducedMotion || slides.length < 2 || slideshowTimer !== null) {
                return;
            }

            slideshowTimer = window.setInterval(() => {
                activeSlide = (activeSlide + 1) % slides.length;
                showSlide(activeSlide);
            }, 6200);
        };
        const stopSlideshow = () => {
            if (slideshowTimer !== null) {
                window.clearInterval(slideshowTimer);
                slideshowTimer = null;
            }
        };
        const handleVisibility = () => {
            if (document.hidden) {
                stopSlideshow();
            } else {
                startSlideshow();
            }
        };

        showSlide(activeSlide);
        updateHeader();
        startSlideshow();
        window.addEventListener("scroll", requestHeaderUpdate, { passive: true });
        document.addEventListener("visibilitychange", handleVisibility);

        let revealObserver: IntersectionObserver | null = null;

        if (prefersReducedMotion || !("IntersectionObserver" in window)) {
            revealNodes.forEach((node) => {
                node.setAttribute("data-nocturne-revealed", "true");
            });
        } else {
            revealObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    entry.target.setAttribute("data-nocturne-revealed", "true");
                    revealObserver?.unobserve(entry.target);
                });
            }, { threshold: 0.12 });
        revealNodes.forEach((node) => revealObserver?.observe(node));
        }

        const galleryCleanup = galleryTiles.map((tile, index) => {
            const previousRole = tile.getAttribute("role");
            const previousTabIndex = tile.getAttribute("tabindex");
            const previousLabel = tile.getAttribute("aria-label");
            const item = nextGalleryItems[index];
            const openGallery = () => {
                if (!item) {
                    return;
                }

                galleryLastFocusedRef.current = document.activeElement instanceof HTMLElement
                    ? document.activeElement
                    : null;
                setGalleryItems(nextGalleryItems);
                setGalleryIndex(index);
            };
            const handleGalleryClick = (event: MouseEvent) => {
                event.preventDefault();
                openGallery();
            };
            const handleGalleryKeyDown = (event: KeyboardEvent) => {
                if (event.key !== "Enter" && event.key !== " ") {
                    return;
                }

                event.preventDefault();
                openGallery();
            };

            tile.setAttribute("role", "button");
            tile.setAttribute("tabindex", "0");
            tile.setAttribute("aria-label", `${galleryLabels.enlarge}: ${item?.alt ?? "Nocturne Club"}`);
            tile.addEventListener("click", handleGalleryClick);
            tile.addEventListener("keydown", handleGalleryKeyDown);

            return () => {
                tile.removeEventListener("click", handleGalleryClick);
                tile.removeEventListener("keydown", handleGalleryKeyDown);
                if (previousRole === null) tile.removeAttribute("role");
                else tile.setAttribute("role", previousRole);
                if (previousTabIndex === null) tile.removeAttribute("tabindex");
                else tile.setAttribute("tabindex", previousTabIndex);
                if (previousLabel === null) tile.removeAttribute("aria-label");
                else tile.setAttribute("aria-label", previousLabel);
            };
        });

        return () => {
            window.removeEventListener("scroll", requestHeaderUpdate);
            document.removeEventListener("visibilitychange", handleVisibility);
            stopSlideshow();
            revealObserver?.disconnect();
            galleryCleanup.forEach((cleanup) => cleanup());
            if (animationFrame !== null) {
                window.cancelAnimationFrame(animationFrame);
            }
            nextHeader?.removeAttribute(HEADER_SCROLLED_ATTRIBUTE);
            scrollCue?.style.removeProperty(HERO_CUE_OPACITY_PROPERTY);
        };
    }, [galleryLabels]);

    useEffect(() => {
        if (galleryIndex === null) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        const handleGalleryKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeGallery();
            } else if (event.key === "ArrowLeft") {
                event.preventDefault();
                showGalleryOffset(-1);
            } else if (event.key === "ArrowRight") {
                event.preventDefault();
                showGalleryOffset(1);
            } else if (event.key === "Tab") {
                const focusable = Array.from(
                    galleryDialogRef.current?.querySelectorAll<HTMLElement>(
                        'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
                    ) ?? [],
                ).filter((element) => element.getClientRects().length > 0);

                if (focusable.length === 0) {
                    event.preventDefault();
                    return;
                }

                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };

        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", handleGalleryKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleGalleryKeyDown);
        };
    }, [closeGallery, galleryIndex, showGalleryOffset]);

    useWebMobileMenuOverlay({
        header,
        isOpen,
        navigationId,
        openAttribute: "data-nocturne-mobile-menu-open",
        setIsOpen,
        templateCode: "nocturne-club",
        toggleRef,
    });

    return (
        <>
            <span
                aria-hidden="true"
                data-nocturne-mobile-menu-runtime="true"
                ref={sentinelRef}
                style={{ display: "none" }}
            />
            {header
                ? createPortal(
                    <button
                        aria-controls={navigationId}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? labels.closeMenu : labels.openMenu}
                        className="nocturne-mobile-menu-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        ref={toggleRef}
                        type="button"
                    >
                        <span />
                        <span />
                        <span />
                    </button>,
                    header,
                )
                : null}
            {galleryIndex !== null && galleryItems[galleryIndex]
                ? createPortal(
                    <div
                        aria-label={galleryLabels.title}
                        aria-modal="true"
                        className="nocturne-gallery-lightbox"
                        ref={galleryDialogRef}
                        role="dialog"
                        style={designCssVars}
                    >
                        <button
                            aria-label={galleryLabels.close}
                            className="nocturne-gallery-backdrop"
                            onClick={closeGallery}
                            type="button"
                        />
                        <div className="nocturne-gallery-shell">
                            <button
                                aria-label={galleryLabels.close}
                                autoFocus
                                className="nocturne-gallery-close"
                                onClick={closeGallery}
                                type="button"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                            <button
                                aria-label={galleryLabels.previous}
                                className="nocturne-gallery-nav nocturne-gallery-nav-prev"
                                onClick={() => showGalleryOffset(-1)}
                                type="button"
                            >
                                <span aria-hidden="true">‹</span>
                            </button>
                            <figure
                                className="nocturne-gallery-figure"
                                onPointerCancel={() => {
                                    galleryPointerStartRef.current = null;
                                }}
                                onPointerDown={(event) => {
                                    galleryPointerStartRef.current = event.clientX;
                                }}
                                onPointerUp={(event) => {
                                    const pointerStart = galleryPointerStartRef.current;
                                    galleryPointerStartRef.current = null;

                                    if (pointerStart === null) {
                                        return;
                                    }

                                    const difference = event.clientX - pointerStart;
                                    if (Math.abs(difference) >= 50) {
                                        showGalleryOffset(difference < 0 ? 1 : -1);
                                    }
                                }}
                            >
                                <div className="nocturne-gallery-image-wrap">
                                    <img
                                        alt={galleryItems[galleryIndex].alt}
                                        className="nocturne-gallery-image"
                                        src={galleryItems[galleryIndex].src}
                                    />
                                </div>
                                <figcaption className="nocturne-gallery-meta">
                                    <span>{galleryItems[galleryIndex].alt}</span>
                                    <strong>{galleryIndex + 1} / {galleryItems.length}</strong>
                                </figcaption>
                            </figure>
                            <button
                                aria-label={galleryLabels.next}
                                className="nocturne-gallery-nav nocturne-gallery-nav-next"
                                onClick={() => showGalleryOffset(1)}
                                type="button"
                            >
                                <span aria-hidden="true">›</span>
                            </button>
                        </div>
                    </div>,
                    document.body,
                )
                : null}
        </>
    );
}
